/**
 * StoryArticle (web) — renders a story's content blocks in order: text (markdown w/ preview links), data
 * (1 → full-width image card, >1 → a horizontal carousel of them), section headers (anchored for the
 * nav), and images (1 → inline, >1 → a horizontal carousel). Reuses the chat `SimpleMarkdown` so markdown
 * + preview-link handling matches the rest of the app.
 */
import type { MouseEvent } from 'react';

import type { Story, StoryEntityRef, StoryImage } from '@elara/client-core/stories/stories';

import { shouldLetBrowserHandleClick } from '@elara/client-core/lib/route-utils';
import { sectionSlug } from '@elara/client-core/states/StoriesState';

import { useRouterState, useStoriesState, useTripsState } from '#states/hooks.js';
import { AppImage } from '#ui';
import { CarouselRow } from '#ui/CarouselRow.js';
import { MapWidget } from '#ui/chat/MapWidget.js';
import { SimpleMarkdown } from '#ui/chat/SimpleMarkdown.js';
import { ImageCard } from '#views/ImageCard.js';
import { storyImageProps } from '#views/stories/story-assets.js';

function StoryDataBlock({ items, showMap = false }: { items: StoryEntityRef[]; showMap?: boolean }) {
  const stories = useStoriesState();
  const tripsState = useTripsState();
  const routerState = useRouterState();

  // Open the matching hotel/place/destination guide in the global preview panel. Mirrors the
  // chat PreviewLink pattern: a real href (cmd/middle-click → new tab) plus an in-page open.
  const guideLink = (item: StoryEntityRef) => ({
    href: routerState.buildUrlWithHashParams({ preview: item.type, id: item.id }),
    onClick: (e: MouseEvent) => {
      if (shouldLetBrowserHandleClick(e)) return;
      e.preventDefault();
      tripsState.showGuide(item.type, item.id);
    },
  });

  const cards =
    items.length === 1 ? (
      (() => {
        const item = items[0]!;
        const resolved = stories.resolveDataItem(item);
        return (
          <ImageCard
            title={resolved.title ?? ''}
            secondaryText={resolved.secondaryText}
            image={resolved.imageUrl}
            orientation="landscape"
            size="large"
            {...guideLink(item)}
          />
        );
      })()
    ) : (
      <CarouselRow label="recommendations" contentClassName="pb-2 snap-x" fadeFrom="from-background-secondary">
        {items.map((item) => {
          const resolved = stories.resolveDataItem(item);
          return (
            <div key={`${item.type}:${item.id}`} className="w-60 shrink-0 snap-start">
              <ImageCard
                title={resolved.title ?? ''}
                secondaryText={resolved.secondaryText}
                image={resolved.imageUrl}
                orientation="portrait"
                size="small"
                {...guideLink(item)}
              />
            </div>
          );
        })}
      </CarouselRow>
    );

  if (!showMap) return cards;
  return (
    <div className="flex flex-col gap-3">
      <StoryMapBlock items={items} />
      {cards}
    </div>
  );
}

/**
 * A story `map` block — the entities as markers on one map. Reuses the chat `MapWidget`: its
 * reference-backed points already load each entity's coordinates from Firestore and wire the marker
 * click to `tripsState.showGuide`, the same seam the data cards above use, so a marker opens the very
 * same guide panel its card does.
 */
function StoryMapBlock({ items }: { items: StoryEntityRef[] }) {
  return (
    <MapWidget
      size="lg"
      points={items.map((item) => ({ id: `${item.type}:${item.id}`, referenceType: item.type, referenceId: item.id }))}
    />
  );
}

/**
 * The article column: the page's 1024px less the 256px nav and its gap on desktop, the viewport below
 * that. Generous on purpose — `sizes` only has to be close for the browser to pick the right width.
 */
const ARTICLE_COLUMN_SIZES = '(min-width: 1024px) 728px, 100vw';

function StoryImageBlock({ images }: { images: StoryImage[] }) {
  if (images.length === 1) {
    const img = images[0]!;
    return (
      <figure className="flex flex-col gap-2">
        <AppImage
          {...storyImageProps(img.asset, ARTICLE_COLUMN_SIZES)}
          width={img.asset.width}
          height={img.asset.height}
          alt={img.caption ?? ''}
          className="h-auto w-full rounded object-cover"
        />
        {img.caption && <figcaption className="text-sm text-muted-foreground">{img.caption}</figcaption>}
      </figure>
    );
  }
  return (
    <CarouselRow label="images" contentClassName="pb-2 snap-x" fadeFrom="from-background-secondary">
      {images.map((img, i) => (
        <figure key={i} className="flex w-72 shrink-0 snap-start flex-col gap-2">
          <AppImage
            {...storyImageProps(img.asset, '288px')}
            alt={img.caption ?? ''}
            className="aspect-[4/3] w-full rounded object-cover"
          />
          {img.caption && <figcaption className="text-sm text-muted-foreground">{img.caption}</figcaption>}
        </figure>
      ))}
    </CarouselRow>
  );
}

export function StoryArticle({ story }: { story: Pick<Story, 'content'> }) {
  return (
    <div className="flex flex-col gap-6">
      {story.content.map((block, i) => {
        switch (block.kind) {
          case 'text':
            return <SimpleMarkdown key={i} text={block.markdown} />;
          case 'section':
            // `anchorOnly` keeps the nav entry + scroll anchor but renders no visible heading.
            return block.anchorOnly ? (
              <div key={i} id={sectionSlug(block.title)} className="scroll-mt-24" aria-hidden="true" />
            ) : (
              <h2
                key={i}
                id={sectionSlug(block.title)}
                className="flex scroll-mt-24 items-center gap-2 pt-2 font-display text-2xl"
              >
                {block.title}
              </h2>
            );
          case 'data':
            return <StoryDataBlock key={i} items={block.items} showMap={block.showMap ?? false} />;
          case 'image':
            return <StoryImageBlock key={i} images={block.images} />;
          case 'map':
            return <StoryMapBlock key={i} items={block.items} />;
        }
      })}
    </div>
  );
}
